import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCheckInData, searchCheckInNames, getAllCheckInCards, checkInPerson, uncheckInPerson, getPeople, getCheckIns } from '../../utils/api';

const DEMO_PERSON = {
  id: 'demo',
  name: 'Rebecca Rice',
  team: 'Events',
  passLevel: 'Media',
  badges: ['Media'],
  alsoOn: ['Worship'],
  needsRadio: true,
  checkedInToday: false,
  meals: {
    Thu: { Breakfast: false, Lunch: true, Dinner: true },
    Fri: { Breakfast: true, Lunch: true, Dinner: true },
    Sat: { Breakfast: true, Lunch: true, Dinner: false },
    Sun: { Breakfast: true, Lunch: true, Dinner: false },
  },
};

const DAYS = ['Thu', 'Fri', 'Sat', 'Sun'];
const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner'];

const CheckIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" style={{ width: 14, height: 14 }}>
    <path d="M2.5 7.5L5.5 10.5L11.5 4" stroke="#202020" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// The check-in station: search a person (first name, last name, email — any
// order, partial words fine), review their card, then EXPLICITLY press
// Check In. Nothing is recorded until the button is pressed.
export default function CheckInPage() {
  const { dateParam } = useParams();
  // Demo person ONLY on the explicit /checkin/demo URL — /checkin, /check-in
  // and /sign-in are the real station with live data.
  const isDemo = dateParam === 'demo';

  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [person, setPerson] = useState(null);
  const [checkState, setCheckState] = useState(null); // null | 'saving' | {time, already}
  const searchTimer = useRef(null);
  // Local name directory: loaded once on page open so each keystroke filters
  // in the browser with zero server round-trip. ciMap tracks today's
  // check-ins for the "already in" pill; it updates locally after each
  // check-in and re-syncs each time the station returns to search.
  const directory = useRef(null);
  const ciMap = useRef({});
  // Full pre-built cards (meals, teams, badges) keyed by person id — loaded
  // once in the background so tapping a result renders with zero wait.
  const cardsMap = useRef(null);

  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    Promise.all([getPeople(), getCheckIns().catch(() => ({}))])
      .then(([people, cis]) => {
        if (cancelled) return;
        directory.current = (people || [])
          .filter(p => p.id && p.name)
          .map(p => ({ id: p.id, name: p.name, email: p.email || '', nameLc: String(p.name).toLowerCase() }));
        ciMap.current = cis || {};
      })
      .catch(() => {}); // directory unavailable → server search keeps working
    // Heavier bulk load in parallel: every person's full card. Until it
    // lands (or if the backend doesn't have the action yet), tapping a name
    // falls back to the per-person fetch.
    getAllCheckInCards()
      .then(d => {
        if (cancelled) return;
        const map = {};
        (d?.results || []).forEach(c => { map[c.id] = c; });
        if (Object.keys(map).length) cardsMap.current = map;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isDemo]);

  function localSearch(q) {
    const toks = q.toLowerCase().split(/\s+/).filter(Boolean);
    const today = localToday();
    return directory.current
      .filter(p => toks.every(t => p.nameLc.indexOf(t) !== -1))
      .slice(0, 8)
      .map(p => {
        const c = ciMap.current[p.id];
        const inToday = !!(c && c.date === today);
        return { id: p.id, name: p.name, email: p.email, checkedInToday: inToday, checkedInAt: inToday ? c.time : '' };
      });
  }

  function handleSearchInput(val) {
    setSearchValue(val);
    clearTimeout(searchTimer.current);
    if (!val.trim() || val.trim().length < 2) { setSearchResults([]); return; }
    if (isDemo) {
      setSearchResults([DEMO_PERSON]);
      return;
    }
    if (directory.current) {
      // Instant: filter the preloaded directory, no network call.
      setSearchResults(localSearch(val.trim()));
      return;
    }
    // Directory still loading — fall back to the server name search.
    searchTimer.current = setTimeout(() => {
      setSearching(true);
      searchCheckInNames(val.trim())
        .then(d => setSearchResults(d?.results || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
  }

  function showResult(p) {
    setCheckState(p.checkedInToday ? { time: p.checkedInAt, already: true } : null);
    setSearchValue('');
    setSearchResults([]);
    if (isDemo || p.meals) { setPerson(p); return; }
    const cached = cardsMap.current && cardsMap.current[p.id];
    if (cached) {
      // Instant: pre-built card, with check-in state from the live map (the
      // bulk snapshot may predate check-ins made since page load).
      setPerson({ ...cached, checkedInToday: p.checkedInToday, checkedInAt: p.checkedInAt });
      return;
    }
    // Fallback: show the card immediately, fill in details as they load.
    setPerson({ ...p, _loading: true });
    getCheckInData('', p.id)
      .then(d => {
        const full = d?.results?.[0];
        setPerson(prev => (prev && prev.id === p.id)
          ? (full || { ...p, _loading: false })
          : prev);
        if (full && full.checkedInToday) setCheckState({ time: full.checkedInAt, already: true });
      })
      .catch(() => {
        setPerson(prev => (prev && prev.id === p.id) ? { ...p, _loading: false } : prev);
      });
  }

  function resetCheckin() {
    setPerson(null);
    setCheckState(null);
    setSearchValue('');
    setSearchResults([]);
    // Re-sync today's check-ins in the background so pills reflect other
    // stations too, not just this device.
    if (!isDemo) getCheckIns().then(m => { ciMap.current = m || {}; }).catch(() => {});
  }

  async function handleCheckIn() {
    if (!person || checkState) return;
    if (isDemo) { setCheckState({ time: 'just now', already: false }); return; }
    setCheckState('saving');
    try {
      const r = await checkInPerson(person.id);
      setCheckState({ time: r?.time || '', already: !!r?.already });
      ciMap.current = { ...ciMap.current, [person.id]: { date: localToday(), time: r?.time || '' } };
    } catch (e) {
      console.error('Check-in failed:', e);
      setCheckState(null);
      alert('Check-in did not save — try again.');
    }
  }

  async function handleUndo() {
    if (!person) return;
    if (isDemo) { setCheckState(null); return; }
    try {
      await uncheckInPerson(person.id);
      setCheckState(null);
      setPerson(p => p ? { ...p, checkedInToday: false, checkedInAt: '' } : p);
      const next = { ...ciMap.current };
      delete next[person.id];
      ciMap.current = next;
    } catch (e) {
      console.error('Undo check-in failed:', e);
      alert('Could not undo the check-in — try again.');
    }
  }

  const teamsList = person
    ? [person.team, ...(Array.isArray(person.alsoOn) ? person.alsoOn : [])].filter(Boolean)
    : [];
  const checkedIn = checkState && checkState !== 'saving';

  return (
    <div className="checkin-wrap">
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* Search panel */}
        {!person && (
          <div className="checkin-card">
            <div className="checkin-scan">
              <div className="checkin-heading">Servant Leader Check-In</div>
              <div className="checkin-sub">Search a first or last name.</div>
              <div style={{ position: 'relative' }}>
                <input
                  className="search-bar"
                  type="text"
                  placeholder="Type a name…"
                  value={searchValue}
                  onChange={e => handleSearchInput(e.target.value)}
                  style={{ textAlign: 'left' }}
                  autoComplete="off"
                  autoFocus
                />
                {searching && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
                    Searching…
                  </div>
                )}
                {!searching && searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    background: 'var(--white)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                    zIndex: 50, overflow: 'hidden', maxHeight: 320, overflowY: 'auto',
                  }}>
                    {searchResults.map((r, i) => (
                      <div
                        key={r.id || i}
                        onClick={() => showResult(r)}
                        style={{ padding: '11px 16px', cursor: 'pointer', textAlign: 'left', borderBottom: i < searchResults.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--off)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--white)'; }}
                      >
                        <div>
                          <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{r.name}</div>
                          <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
                            {[r.team, r.email].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        {r.checkedInToday && (
                          <span className="pill pill-green" style={{ fontSize: 9, padding: '3px 8px', flexShrink: 0 }}>✓ In {r.checkedInAt}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {!searching && searchValue.trim().length >= 2 && searchResults.length === 0 && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
                    No results found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Person card */}
        {person && (
          <div className="checkin-card">
            {checkedIn && (
              <div style={{ background: '#1a5c3a', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 10, borderRadius: 'var(--radius) var(--radius) 0 0' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="white" fillOpacity="0.2"/>
                  <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontFamily: 'var(--font-h)', fontWeight: 700, fontSize: 13, color: 'white' }}>
                  {checkState.already ? `Already checked in${checkState.time ? ` at ${checkState.time}` : ''}` : `Checked in${checkState.time ? ` at ${checkState.time}` : ''}`}
                </span>
              </div>
            )}

            <div className="checkin-result">
              <div className="result-name">{person.name}</div>

              <div className="result-row">
                <span className="result-row-label">Pass Level</span>
                <span className="result-row-val">
                  {person._loading ? <span style={{ color: 'var(--muted)', fontWeight: 300 }}>Loading…</span> : (() => {
                    const badges = (person.badges && person.badges.length)
                      ? person.badges
                      : String(person.passLevel || '').split(',').map(b => b.trim()).filter(Boolean);
                    return badges.length
                      ? badges.map(b => (
                          <span
                            key={b}
                            className="pill pill-dark"
                            style={/kids/i.test(b) ? { background: '#6AA292', marginRight: 4 } : { marginRight: 4 }}
                          >
                            {b}
                          </span>
                        ))
                      : <span style={{ color: 'var(--muted)' }}>—</span>;
                  })()}
                </span>
              </div>

              {person.needsRadio && (
                <div className="result-row">
                  <span className="result-row-label">Radio</span>
                  <span className="result-row-val" style={{ color: '#b5700a' }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 4 }}>
                      <rect x="4" y="1" width="6" height="10" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                      <line x1="7" y1="11" x2="7" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="5" y1="13" x2="9" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Needs Radio
                  </span>
                </div>
              )}

              {/* Meals: rows = days, columns = Breakfast/Lunch/Dinner. A black
                  check marks each meal the person actually gets. */}
              <div className="result-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10, paddingBottom: 20 }}>
                <span className="result-row-label">Meals</span>
                {person._loading ? (
                  <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', padding: '8px 0' }}>Loading meals…</div>
                ) : (
                <table className="checkin-meal-table">
                  <thead>
                    <tr>
                      <th></th>
                      {DAYS.map(d => <th key={d}>{d}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {MEAL_TYPES.map(m => (
                      <tr key={m}>
                        <td className="cmt-day">{m}</td>
                        {DAYS.map(d => (
                          <td key={d} className="cmt-cell">
                            {person.meals?.[d]?.[m] ? <CheckIcon /> : <span className="cmt-empty" />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
              </div>

              <div className="result-row" style={{ borderBottom: 'none' }}>
                <span className="result-row-label">Teams</span>
                <span className="result-row-val">{person._loading ? <span style={{ color: 'var(--muted)', fontWeight: 300 }}>Loading…</span> : (teamsList.length ? teamsList.join(', ') : '—')}</span>
              </div>
            </div>

            <div style={{ padding: '0 40px 32px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!checkedIn && (
                <button
                  className="btn btn-dark"
                  style={{ width: '100%', padding: 16, fontSize: 15 }}
                  disabled={checkState === 'saving' || person._loading}
                  onClick={handleCheckIn}
                >
                  {checkState === 'saving' ? 'Checking In…' : 'Check In'}
                </button>
              )}
              <button className="btn" style={{ width: '100%', padding: 14 }} onClick={resetCheckin}>
                {checkedIn ? 'Check In Another' : 'Back to Search'}
              </button>
              {checkedIn && (
                <button
                  onClick={handleUndo}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', textDecoration: 'underline', padding: 4, alignSelf: 'center' }}
                  title="Remove this check-in if it was made by mistake"
                >
                  Undo check-in
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
